import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

const Header16 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template16' },
        { label: 'Shop', path: '/template16/shop' },
        { label: 'About', path: '/template16/about' },
        { label: 'Contact', path: '/template16/contact' },
        { label: 'Orders', path: '/template16/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500;600;700&family=Poppins:wght@300;400;500&display=swap');
            `}</style>

            <header
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: scrolled ? 'rgba(255, 248, 240, 0.97)' : '#FFF8F0',
                    borderBottom: scrolled ? '1px solid rgba(184, 134, 11, 0.2)' : '1px solid transparent',
                    transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
                    boxShadow: scrolled ? '0 2px 20px rgba(153, 27, 27, 0.06)' : 'none',
                }}
            >
                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '80px',
                    }}
                >
                    {/* Left: Brand */}
                    <h1
                        onClick={() => navigate('/template16')}
                        style={{
                            fontFamily: "'EB Garamond', serif",
                            fontSize: '28px',
                            fontWeight: 600,
                            color: '#991B1B',
                            cursor: 'pointer',
                            letterSpacing: '3px',
                            margin: 0,
                        }}
                    >
                        DULHAN
                        <span style={{ color: '#B8860B', fontSize: '10px', display: 'block', letterSpacing: '4px', fontWeight: 400, marginTop: '-4px' }}>
                            BRIDAL COUTURE
                        </span>
                    </h1>

                    {/* Center: Nav Links (Desktop) */}
                    <nav
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '36px',
                        }}
                        className="dulhan-desktop-nav"
                    >
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                style={{
                                    fontFamily: "'EB Garamond', serif",
                                    fontSize: '15px',
                                    fontWeight: 500,
                                    color: '#1A1A1A',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    letterSpacing: '1.5px',
                                    textTransform: 'uppercase',
                                    transition: 'color 0.3s ease',
                                }}
                                onMouseEnter={(e) => (e.target.style.color = '#991B1B')}
                                onMouseLeave={(e) => (e.target.style.color = '#1A1A1A')}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right: Icons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <Search
                            size={20}
                            style={{ color: '#1A1A1A', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#991B1B')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#1A1A1A')}
                        />
                        <User
                            size={20}
                            style={{ color: '#1A1A1A', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onClick={() => navigate('/template16/profile')}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#991B1B')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#1A1A1A')}
                        />
                        <div
                            style={{ position: 'relative', cursor: 'pointer' }}
                            onClick={() => navigate('/template16/cart')}
                        >
                            <ShoppingCart
                                size={20}
                                style={{ color: '#1A1A1A', transition: 'color 0.3s ease' }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = '#991B1B')}
                                onMouseLeave={(e) => (e.currentTarget.style.color = '#1A1A1A')}
                            />
                            {cartCount > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        backgroundColor: '#991B1B',
                                        color: '#FFFFFF',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        fontFamily: "'Poppins', sans-serif",
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {cartCount}
                                </span>
                            )}
                        </div>

                        {/* Mobile Hamburger */}
                        <button
                            className="dulhan-mobile-menu-btn"
                            onClick={() => setMobileMenuOpen(true)}
                            style={{
                                display: 'none',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            <Menu size={24} style={{ color: '#991B1B' }} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Full-Screen Overlay */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 100,
                    backgroundColor: '#FFF8F0',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: mobileMenuOpen ? 1 : 0,
                    pointerEvents: mobileMenuOpen ? 'auto' : 'none',
                    transition: 'opacity 0.3s ease',
                }}
            >
                {/* Decorative gold line at top */}
                <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '60px', height: '3px', backgroundColor: '#B8860B', marginTop: '20px' }} />

                <button
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                        position: 'absolute',
                        top: '24px',
                        right: '24px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                    }}
                >
                    <X size={28} style={{ color: '#991B1B' }} />
                </button>

                <nav style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '32px' }}>
                    {navLinks.map((link) => (
                        <button
                            key={link.label}
                            onClick={() => handleNavClick(link.path)}
                            style={{
                                fontFamily: "'EB Garamond', serif",
                                fontSize: '28px',
                                fontWeight: 500,
                                color: '#991B1B',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                letterSpacing: '3px',
                                textTransform: 'uppercase',
                                transition: 'color 0.3s ease',
                            }}
                            onMouseEnter={(e) => (e.target.style.color = '#B8860B')}
                            onMouseLeave={(e) => (e.target.style.color = '#991B1B')}
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Responsive Styles */}
            <style>{`
                .dulhan-desktop-nav {
                    display: flex !important;
                }
                .dulhan-mobile-menu-btn {
                    display: none !important;
                }
                @media (max-width: 768px) {
                    .dulhan-desktop-nav {
                        display: none !important;
                    }
                    .dulhan-mobile-menu-btn {
                        display: block !important;
                    }
                }
            `}</style>
        </>
    );
};

export default Header16;
